import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  Plus, Trash, User, MagnifyingGlass, PencilSimple,
  ToggleLeft, ToggleRight, Eye, CircleNotch, Key, ArrowsClockwise, DownloadSimple
} from '@phosphor-icons/react';
import AdminLayout from '../components/AdminLayout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

import API_URL from '../config';
import { MattersToAddCheck } from '../components/MattersToAddCheck';

function LastSeen({ iso }) {
  if (!iso) {
    return (
      <span className="hidden md:inline-block text-xs px-2 py-1" style={{ color: '#9a9488', backgroundColor: '#f2e9db' }} title="Client has never signed in">
        Never signed in
      </span>
    );
  }
  const when = new Date(iso);
  const days = Math.floor((Date.now() - when.getTime()) / (1000 * 60 * 60 * 24));
  let label;
  if (days <= 0) label = 'Seen today';
  else if (days === 1) label = 'Seen yesterday';
  else if (days < 7) label = `${days}d ago`;
  else if (days < 30) label = `${Math.floor(days / 7)}w ago`;
  else label = `${Math.floor(days / 30)}mo ago`;
  const stale = days >= 30;
  return (
    <span
      className="hidden md:inline-block text-xs px-2 py-1"
      style={stale
        ? { color: '#b45309', backgroundColor: 'rgba(255, 209, 0, 0.18)' }
        : { color: '#166534', backgroundColor: 'rgba(34, 197, 94, 0.12)' }}
      title={`Last sign-in: ${when.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })} CT`}
    >
      {label}
    </span>
  );
}

function coverageEndText(client) {
  if (!client.pp_coverage_synced) return 'not synced yet';
  const v = client.pp_coverage_end;
  if (!v) return 'no end date';
  const d = /^\d+$/.test(String(v)) ? new Date(Number(v)) : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return `ends ${d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
}

export default function AdminClients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';
  const setFilter = (f) => {
    const next = new URLSearchParams(searchParams);
    if (f === 'all') next.delete('filter'); else next.set('filter', f);
    setSearchParams(next, { replace: true });
  };
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [hubspotContacts, setHubspotContacts] = useState([]);
  const [hubspotSearch, setHubspotSearch] = useState('');
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotConnected, setHubspotConnected] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [resyncingId, setResyncingId] = useState(null);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // {running, last_finished_at, last_result}
  const [nowTick, setNowTick] = useState(Date.now());
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [pendingActivation, setPendingActivation] = useState(null); // client object pending activation confirm

  const [formData, setFormData] = useState({
    email: '', password: '', name: '', company: '',
    is_active: false, has_protection_plan: false,
    hubspot_contact_id: '', gmail_search_query: '', drive_folder_id: ''
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [sendLink, setSendLink] = useState('none'); // none | reset | welcome (edit dialog)

  useEffect(() => {
    fetchClients();
    checkHubspotStatus();
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/discover-matters/status`);
      setSyncStatus(res.data);
      return res.data;
    } catch (e) {
      return null;
    }
  }, []);

  // Poll sync status: every 5s while a sync is running, then refresh the client list once it finishes.
  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  useEffect(() => {
    if (!syncStatus?.running) return;
    const id = setInterval(async () => {
      const s = await fetchSyncStatus();
      if (s && !s.running) { clearInterval(id); fetchClients(); }
    }, 5000);
    return () => clearInterval(id);
  }, [syncStatus?.running, fetchSyncStatus]);

  // Tick every 30s so the "X min ago" label stays fresh.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const syncLabel = (() => {
    if (syncStatus?.running) return { text: 'Syncing from HubSpot…', spin: true };
    const iso = syncStatus?.last_finished_at;
    if (!iso) return null;
    const mins = Math.floor((nowTick - new Date(iso).getTime()) / 60000);
    let when;
    if (mins <= 0) when = 'just now';
    else if (mins === 1) when = '1 min ago';
    else if (mins < 60) when = `${mins} min ago`;
    else if (mins < 1440) when = `${Math.floor(mins / 60)}h ago`;
    else when = `${Math.floor(mins / 1440)}d ago`;
    const r = syncStatus?.last_result || {};
    const extra = (r.matters_added || r.stage_changes)
      ? ` · ${r.matters_added || 0} new, ${r.stage_changes || 0} stage change(s)`
      : '';
    return { text: `Last synced ${when}${extra}`, spin: false };
  })();

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDemo = async () => {
    setSeedingDemo(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/seed-demo-client`);
      toast.success(`Demo client ready — ${res.data.matter_count} sample matters. Use "Preview" to view.`);
      fetchClients();
    } catch (error) {
      toast.error('Failed to create demo client');
    } finally {
      setSeedingDemo(false);
    }
  };

  const handleDownloadMapping = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/stage-mapping.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'indie-law-stage-mapping.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download stage mapping');
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/discover-matters`, {});
      const d = res.data || {};
      if (d.status === 'running') {
        toast.info(d.message || 'A HubSpot sync is already in progress — please wait for it to finish.');
      } else {
        toast.success(d.message || 'HubSpot sync started — running in the background. Refresh in a couple of minutes.');
      }
      fetchSyncStatus();
    } catch (error) {
      toast.error('Failed to start HubSpot sync');
    } finally {
      setRefreshingAll(false);
    }
  };

  const checkHubspotStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/hubspot/status`);
      setHubspotConnected(response.data.connected);
    } catch (error) {
      setHubspotConnected(false);
    }
  };

  const searchHubspot = useCallback(async (query) => {
    if (!hubspotConnected) return;
    setHubspotLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/hubspot/contacts`, {
        params: { q: query }
      });
      setHubspotContacts(response.data.contacts || []);
    } catch (error) {
      console.error('HubSpot search error:', error);
    } finally {
      setHubspotLoading(false);
    }
  }, [hubspotConnected]);

  const handleHubspotSelect = (contact) => {
    setFormData(prev => ({
      ...prev,
      email: contact.email || prev.email,
      name: [contact.firstname, contact.lastname].filter(Boolean).join(' ') || prev.name,
      company: contact.company || prev.company,
      hubspot_contact_id: contact.id,
      gmail_search_query: contact.email ? `from:${contact.email} OR to:${contact.email}` : prev.gmail_search_query
    }));
    setHubspotContacts([]);
    setHubspotSearch('');
  };

  const resetForm = () => {
    setFormData({
      email: '', password: '', name: '', company: '',
      is_active: false, has_protection_plan: false,
      hubspot_contact_id: '', gmail_search_query: '', drive_folder_id: ''
    });
    setHubspotContacts([]);
    setHubspotSearch('');
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/clients`, formData);
      const pwd = response.data.generated_password;
      setGeneratedPassword({ password: pwd, email: formData.email, name: formData.name });
      toast.success('Client created successfully');
      setIsCreateDialogOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create client';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setSendLink('none');
    setFormData({
      email: client.email, password: '',
      name: client.name, company: client.company || '',
      is_active: client.is_active,
      has_protection_plan: client.has_protection_plan || false,
      hubspot_contact_id: client.hubspot_contact_id || '',
      gmail_search_query: client.gmail_search_query || '',
      drive_folder_id: client.drive_folder_id || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await axios.put(`${API_URL}/api/admin/clients/${selectedClient.id}`, {
        name: formData.name, company: formData.company || null,
        is_active: formData.is_active, has_protection_plan: formData.has_protection_plan,
        hubspot_contact_id: formData.hubspot_contact_id || null,
        gmail_search_query: formData.gmail_search_query || null,
        drive_folder_id: formData.drive_folder_id || null
      });
      const emailChanged = (formData.email || '').trim().toLowerCase() !== (selectedClient.email || '').toLowerCase();
      if (emailChanged || sendLink !== 'none') {
        const res = await axios.post(`${API_URL}/api/admin/clients/${selectedClient.id}/update-email`, {
          new_email: (formData.email || '').trim(),
          send_link: sendLink,
        });
        toast.success(res.data.message || 'Client updated');
      } else {
        toast.success('Client updated');
      }
      setIsEditDialogOpen(false);
      setSelectedClient(null);
      resetForm();
      setSendLink('none');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally {
      setUpdating(false);
    }
  };

  const handlePreviewClient = async (clientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/preview-client/${clientId}`);
      const { preview_token, client_name } = response.data;
      window.open(`${window.location.origin}/dashboard?preview_token=${preview_token}&preview_name=${encodeURIComponent(client_name)}`, '_blank');
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const handleToggleActive = async (clientId) => {
    setTogglingId(clientId);
    try {
      const response = await axios.post(`${API_URL}/api/admin/clients/${clientId}/toggle-active`, {});
      const { is_active, message, password, email, webhook } = response.data;
      // If a fresh password was returned (inactive → active), surface it to the admin
      // as a copyable safety net regardless of whether the webhook fired successfully.
      if (password) {
        setGeneratedPassword({
          email,
          password,
          name: pendingActivation?.name,
          webhookSent: !!webhook?.sent,
          webhookReason: webhook?.reason,
        });
      }
      if (webhook && webhook.sent === false) {
        toast.warning(message || 'Activated, but the welcome email did not send. Copy the password and send manually.');
      } else {
        toast.success(message || (is_active ? 'Client activated' : 'Client deactivated'));
      }
      fetchClients();
    } catch (error) {
      toast.error('Failed to toggle client status');
    } finally {
      setTogglingId(null);
      setPendingActivation(null);
    }
  };

  const handleToggleClick = (client) => {
    // Going inactive → active triggers an outbound email; require explicit confirmation.
    if (!client.is_active) {
      setPendingActivation(client);
      return;
    }
    // Deactivation is reversible and silent — proceed immediately.
    handleToggleActive(client.id);
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Delete this client and all their matters?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/clients/${clientId}`);
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleResetPassword = async (clientId, clientName) => {
    if (!window.confirm(`Reset password for ${clientName}? The new password will be shown once — make sure to copy it.`)) return;
    try {
      const response = await axios.post(`${API_URL}/api/admin/clients/${clientId}/reset-password`, {});
      setGeneratedPassword({
        email: response.data.email,
        password: response.data.password,
        name: response.data.name,
      });
      toast.success('Password reset');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleResyncHubspot = async (clientId) => {
    setResyncingId(clientId);
    try {
      const response = await axios.post(`${API_URL}/api/admin/clients/${clientId}/resync-hubspot`, {});
      toast.success(response.data.message || 'Synced from HubSpot');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'HubSpot re-sync failed');
    } finally {
      setResyncingId(null);
    }
  };

  const matchesChip = (c) => {
    if (filter === 'pp') return !!c.has_protection_plan;
    if (filter === 'pp-never') return !!c.has_protection_plan && !c.last_seen_at;
    if (filter === 'monitoring') return !!c.has_monitoring;
    return true;
  };
  const matchesSearch = (c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.company && c.company.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredClients = clients.filter(c => matchesChip(c) && matchesSearch(c));

  const counts = {
    all: clients.length,
    pp: clients.filter(c => c.has_protection_plan).length,
    'pp-never': clients.filter(c => c.has_protection_plan && !c.last_seen_at).length,
    monitoring: clients.filter(c => c.has_monitoring).length,
  };
  const chips = [
    { key: 'all', label: 'All' },
    { key: 'pp', label: 'Protection Plan' },
    { key: 'pp-never', label: 'Protection Plan · never signed in' },
    { key: 'monitoring', label: 'Has monitoring results' },
  ];

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#ffd100] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-clients">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>Manage</p>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>
              Clients
            </h1>
            <p className="text-sm mt-1" style={{ color: '#9a9488' }}>
              {clients.filter(c => c.is_active).length} active / {clients.length} total
            </p>
            {syncLabel && (
              <p className="text-xs mt-1 flex items-center gap-1.5" data-testid="sync-status-indicator"
                style={{ color: syncLabel.spin ? '#1d57a5' : '#9a9488' }}>
                {syncLabel.spin && <CircleNotch weight="bold" className="w-3.5 h-3.5 animate-spin" />}
                {syncLabel.text}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshAll}
              disabled={refreshingAll}
              className="btn-secondary flex items-center gap-2 disabled:opacity-60"
              data-testid="refresh-all-hubspot-btn"
              title="Re-sync every active client from HubSpot now — pulls new matters and refreshes tracker stages"
            >
              {refreshingAll
                ? <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                : <ArrowsClockwise weight="bold" className="w-4 h-4" />}
              {refreshingAll ? 'Syncing…' : 'Refresh from HubSpot'}
            </button>
            <MattersToAddCheck />
            <button
              onClick={handleDownloadMapping}
              className="btn-secondary flex items-center gap-2"
              data-testid="download-stage-mapping-btn"
              title="Download the HubSpot-stage → client-view mapping as a CSV"
            >
              <DownloadSimple weight="bold" className="w-4 h-4" />
              Stage Mapping
            </button>
            <button
              onClick={handleSeedDemo}
              disabled={seedingDemo}
              className="btn-secondary flex items-center gap-2 disabled:opacity-60"
              data-testid="seed-demo-client-btn"
              title="Create or refresh the 'Sample Client (Demo)' account with example matters"
            >
              {seedingDemo
                ? <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                : <ArrowsClockwise weight="bold" className="w-4 h-4" />}
              {seedingDemo ? 'Working…' : 'Demo Client'}
            </button>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <button className="btn-primary flex items-center gap-2" data-testid="create-client-btn">
                  <Plus weight="bold" className="w-4 h-4" />
                  Add New Client
                </button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                  Create New Client
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                {/* HubSpot Lookup */}
                {hubspotConnected && (
                  <div className="p-4 border mb-2" style={{ backgroundColor: '#f2e9db', borderColor: '#d4cfc5' }}>
                    <label className="label-uppercase block mb-2">Import from HubSpot</label>
                    <div className="flex gap-2">
                      <input type="text" value={hubspotSearch}
                        onChange={(e) => setHubspotSearch(e.target.value)}
                        className="input-swiss flex-1" placeholder="Search by name or email..."
                        data-testid="hubspot-search-input" />
                      <button type="button" onClick={() => searchHubspot(hubspotSearch)}
                        disabled={hubspotLoading} className="btn-secondary flex items-center gap-1"
                        data-testid="hubspot-search-btn">
                        {hubspotLoading ? <CircleNotch weight="bold" className="w-4 h-4 animate-spin" /> : <MagnifyingGlass weight="bold" className="w-4 h-4" />}
                        Search
                      </button>
                    </div>
                    {hubspotContacts.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto border bg-white" style={{ borderColor: '#d4cfc5' }}>
                        {hubspotContacts.map((contact) => (
                          <button key={contact.id} type="button" onClick={() => handleHubspotSelect(contact)}
                            className="w-full text-left px-3 py-2 hover:bg-[#f2e9db] border-b last:border-0" style={{ borderColor: '#d4cfc5' }}
                            data-testid={`hubspot-contact-${contact.id}`}>
                            <p className="text-sm font-medium" style={{ color: '#101820' }}>{contact.firstname} {contact.lastname}</p>
                            <p className="text-xs" style={{ color: '#9a9488' }}>{contact.email} {contact.company && `• ${contact.company}`}</p>
                          </button>
                        ))}
                      </div>
                    )}
                    {formData.hubspot_contact_id && (
                      <p className="mt-2 text-xs text-[#22C55E]">HubSpot contact linked (ID: {formData.hubspot_contact_id})</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label-uppercase block mb-2">Full Name</label>
                    <input type="text" value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      className="input-swiss" placeholder="John Doe" required data-testid="client-name-input" />
                  </div>
                  <div>
                    <label className="label-uppercase block mb-2">Company</label>
                    <input type="text" value={formData.company}
                      onChange={(e) => handleFieldChange('company', e.target.value)}
                      className="input-swiss" placeholder="Company name" data-testid="client-company-input" />
                  </div>
                </div>

                <div>
                  <label className="label-uppercase block mb-2">Email (Login)</label>
                  <input type="email" value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    className="input-swiss" placeholder="john@company.com" required data-testid="client-email-input" />
                  <p className="text-xs mt-1" style={{ color: '#9a9488' }}>A secure password will be auto-generated</p>
                </div>

                <div className="border-t pt-4 mt-4" style={{ borderColor: '#d4cfc5' }}>
                  <p className="label-uppercase mb-3">Client Configuration</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium block mb-1" style={{ color: '#101820' }}>Google Drive Folder</label>
                      <input type="text" value={formData.drive_folder_id}
                        onChange={(e) => handleFieldChange('drive_folder_id', e.target.value)}
                        className="input-swiss text-sm" placeholder="Paste the Google Drive folder URL"
                        data-testid="client-drive-folder-input" />
                      <p className="text-xs mt-1" style={{ color: '#9a9488' }}>Paste the link to the client's folder in Google Drive</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between border-t pt-4" style={{ borderColor: '#d4cfc5' }}>
                  <div className="flex items-center gap-3">
                    <Switch checked={formData.is_active}
                      onCheckedChange={(checked) => handleFieldChange('is_active', checked)}
                      data-testid="client-active-switch" />
                    <p className="text-sm font-medium" style={{ color: '#101820' }}>
                      {formData.is_active ? 'Active — Client can log in' : 'Draft — Client cannot log in yet'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Switch checked={formData.has_protection_plan}
                      onCheckedChange={(checked) => handleFieldChange('has_protection_plan', checked)}
                      data-testid="client-protection-plan-switch" />
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#101820' }}>
                        {formData.has_protection_plan ? 'Protection Plan — Active' : 'No Protection Plan'}
                      </p>
                      <p className="text-xs" style={{ color: '#9a9488' }}>
                        {formData.has_protection_plan ? 'Client sees renewal deadlines & monitoring info' : 'Client sees Protection Plan promo page'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setIsCreateDialogOpen(false); resetForm(); }}
                    className="btn-secondary flex-1">Cancel</button>
                  <button type="submit" disabled={creating}
                    className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="submit-client-btn">
                    {creating ? <><div className="w-4 h-4 border-2 border-[#141b4d]/20 border-t-[#141b4d] rounded-full animate-spin"></div>Creating...</> : 'Create Client'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2" data-testid="client-filter-chips">
          {chips.map((chip) => {
            const active = filter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => setFilter(chip.key)}
                data-testid={`filter-chip-${chip.key}`}
                className="text-sm px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2"
                style={active
                  ? { backgroundColor: '#141b4d', color: '#fff', borderColor: '#141b4d' }
                  : { backgroundColor: '#fff', color: '#3f3f46', borderColor: '#d4cfc5' }}
              >
                {chip.label}
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                  style={active
                    ? { backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }
                    : { backgroundColor: '#f2e9db', color: '#9a9488' }}>
                  {counts[chip.key]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass weight="regular" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#9a9488' }} />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="input-swiss !pl-10" placeholder="Search clients..." data-testid="search-clients-input" />
        </div>

        {/* Clients List */}
        {filteredClients.length === 0 ? (
          <div className="card-swiss text-center py-12">
            <User weight="duotone" className="w-12 h-12 mx-auto mb-4" style={{ color: '#9a9488' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: '#141b4d' }}>{(searchQuery || filter !== 'all') ? 'No clients match' : 'No Clients Yet'}</h3>
            <p style={{ color: '#9a9488' }}>{(searchQuery || filter !== 'all') ? 'Try a different search or filter.' : 'Create your first client to get started.'}</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            {filteredClients.map((client, index) => (
              <motion.div key={client.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`card-swiss flex items-center justify-between ${!client.is_active ? 'opacity-70' : ''}`}
                data-testid={`client-row-${client.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 flex items-center justify-center ${client.is_active ? 'bg-[#22C55E]/10' : ''}`} style={!client.is_active ? { backgroundColor: '#f2e9db' } : {}}>
                    <User weight="duotone" className={`w-6 h-6 ${client.is_active ? 'text-[#22C55E]' : ''}`} style={!client.is_active ? { color: '#9a9488' } : {}} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium" style={{ color: '#101820' }}>{client.name}</p>
                      <span className={`text-xs font-medium px-2 py-0.5 ${client.is_active ? 'bg-[#22C55E]/10 text-[#22C55E]' : ''}`}
                        style={!client.is_active ? { backgroundColor: '#f2e9db', color: '#9a9488' } : {}}>
                        {client.is_active ? 'ACTIVE' : 'DRAFT'}
                      </span>
                      {client.self_signup && (
                        <span className="text-xs font-medium px-2 py-0.5"
                          style={{ backgroundColor: 'rgba(29,87,165,0.10)', color: '#1d57a5' }}
                          title="This client registered themselves via the public Get Started form"
                          data-testid={`self-signup-badge-${client.id}`}>
                          Self-signup
                        </span>
                      )}
                      {client.pending_password && (
                        <span className="text-xs font-medium px-2 py-0.5"
                          style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#b45309' }}
                          title="Client hasn't set their password yet">
                          Password pending
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: '#9a9488' }}>{client.email}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      {client.company && <span className="text-xs" style={{ color: '#9a9488' }}>{client.company}</span>}
                      {client.hubspot_contact_id && <span className="text-xs text-[#F59E0B]">HubSpot linked</span>}
                      {client.has_protection_plan && (
                        <span className="text-xs text-[#22C55E]" data-testid={`pp-badge-${client.id}`}>
                          Protection Plan · {coverageEndText(client)}
                        </span>
                      )}
                      {client.has_monitoring && (
                        <span className="text-xs" style={{ color: '#1d57a5' }} data-testid={`monitoring-summary-${client.id}`}>
                          {client.monitoring_quarter}{client.monitoring_needs_look
                            ? ` · ${client.monitoring_needs_look} need a look`
                            : ' · all clear'}
                        </span>
                      )}
                      {client.gmail_search_query && <span className="text-xs" style={{ color: '#1d57a5' }}>Gmail filtered</span>}
                      {client.drive_folder_id && <span className="text-xs text-[#8B5CF6]">Drive scoped</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {client.is_active && (
                    <LastSeen iso={client.last_seen_at} />
                  )}
                  <button onClick={() => handleResyncHubspot(client.id)}
                    disabled={resyncingId === client.id}
                    className="p-2 hover:bg-[#f2e9db] transition-colors" title="Re-sync from HubSpot (pull latest matters, Drive folder & Protection Plan)"
                    style={{ color: '#9a9488' }}
                    data-testid={`resync-hubspot-${client.id}`}>
                    {resyncingId === client.id ? (
                      <CircleNotch weight="bold" className="w-5 h-5 animate-spin" style={{ color: '#F59E0B' }} />
                    ) : (
                      <ArrowsClockwise weight="regular" className="w-5 h-5" />
                    )}
                  </button>
                  <button onClick={() => handlePreviewClient(client.id)}
                    className="p-2 hover:bg-[#f2e9db] transition-colors" title="Preview as client"
                    style={{ color: '#9a9488' }}
                    data-testid={`preview-client-${client.id}`}>
                    <Eye weight="regular" className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleToggleClick(client)}
                    disabled={togglingId === client.id}
                    className="p-2 hover:bg-[#f2e9db] transition-colors" title={client.is_active ? 'Deactivate' : 'Activate'}
                    data-testid={`toggle-client-${client.id}`}>
                    {togglingId === client.id ? (
                      <CircleNotch weight="bold" className="w-5 h-5 animate-spin" style={{ color: '#9a9488' }} />
                    ) : client.is_active ? (
                      <ToggleRight weight="fill" className="w-6 h-6 text-[#22C55E]" />
                    ) : (
                      <ToggleLeft weight="regular" className="w-6 h-6" style={{ color: '#9a9488' }} />
                    )}
                  </button>
                  <button onClick={() => handleEditClient(client)}
                    className="p-2 hover:bg-[#f2e9db] transition-colors"
                    style={{ color: '#9a9488' }}
                    data-testid={`edit-client-${client.id}`}>
                    <PencilSimple weight="regular" className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleResetPassword(client.id, client.name)}
                    className="p-2 hover:bg-[#f2e9db] transition-colors" title="Reset password"
                    style={{ color: '#9a9488' }}
                    data-testid={`reset-password-${client.id}`}>
                    <Key weight="regular" className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDeleteClient(client.id)}
                    className="p-2 text-[#A1A1AA] hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`delete-client-${client.id}`}>
                    <Trash weight="regular" className="w-5 h-5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) { setSelectedClient(null); resetForm(); } }}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                Edit Client: {selectedClient?.name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateClient} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-uppercase block mb-2">Full Name</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    className="input-swiss" placeholder="John Doe" required data-testid="client-name-input" />
                </div>
                <div>
                  <label className="label-uppercase block mb-2">Company</label>
                  <input type="text" value={formData.company}
                    onChange={(e) => handleFieldChange('company', e.target.value)}
                    className="input-swiss" placeholder="Company name" data-testid="client-company-input" />
                </div>
              </div>

              {/* Login email — editable, fixes wrong-email signups */}
              <div className="border-t pt-4" style={{ borderColor: '#d4cfc5' }}>
                <label className="label-uppercase block mb-2">Email (Login)</label>
                <input type="email" value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className="input-swiss" placeholder="john@company.com" required data-testid="client-email-edit-input" />
                <p className="text-xs mt-1" style={{ color: '#9a9488' }}>
                  Changing this updates their login and re-checks HubSpot for their matters. Pick an access link below to email them a way back in.
                </p>
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-medium" style={{ color: '#101820' }}>Send access link</p>
                  {[
                    { v: 'none', label: "Don't send anything" },
                    { v: 'reset', label: 'Password-reset link (they already set a password)' },
                    { v: 'welcome', label: 'First-time set-password link (new / never logged in)' },
                  ].map((opt) => (
                    <label key={opt.v} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: '#3f3f46' }}>
                      <input type="radio" name="send-link" value={opt.v}
                        checked={sendLink === opt.v}
                        onChange={() => setSendLink(opt.v)}
                        data-testid={`send-link-${opt.v}`} />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4" style={{ borderColor: '#d4cfc5' }}>
                <p className="label-uppercase mb-3">Client Configuration</p>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium block mb-1" style={{ color: '#101820' }}>Google Drive Folder</label>
                    <input type="text" value={formData.drive_folder_id}
                      onChange={(e) => handleFieldChange('drive_folder_id', e.target.value)}
                      className="input-swiss text-sm" placeholder="Paste the Google Drive folder URL"
                      data-testid="client-drive-folder-input" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between border-t pt-4" style={{ borderColor: '#d4cfc5' }}>
                <div className="flex items-center gap-3">
                  <Switch checked={formData.is_active}
                    onCheckedChange={(checked) => handleFieldChange('is_active', checked)}
                    data-testid="client-active-switch" />
                  <p className="text-sm font-medium" style={{ color: '#101820' }}>
                    {formData.is_active ? 'Active — Client can log in' : 'Draft — Client cannot log in yet'}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Switch checked={formData.has_protection_plan}
                    onCheckedChange={(checked) => handleFieldChange('has_protection_plan', checked)}
                    data-testid="client-protection-plan-switch" />
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#101820' }}>
                      {formData.has_protection_plan ? 'Protection Plan — Active' : 'No Protection Plan'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setIsEditDialogOpen(false); resetForm(); }}
                  className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={updating}
                  className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="submit-client-btn">
                  {updating ? <><div className="w-4 h-4 border-2 border-[#141b4d]/20 border-t-[#141b4d] rounded-full animate-spin"></div>Updating...</> : 'Update Client'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Generated Password Dialog */}
        <Dialog open={!!generatedPassword} onOpenChange={(open) => { if (!open) setGeneratedPassword(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                Client Credentials
              </DialogTitle>
            </DialogHeader>
            {generatedPassword && (
              <div className="space-y-4 mt-4" data-testid="generated-password-dialog">
                {generatedPassword.webhookSent ? (
                  <div className="callout-box" style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }}>
                    <p className="text-sm font-medium" style={{ color: '#14532d' }}>
                      ✓ Welcome email queued via Zapier — credentials are also shown below as a safety net.
                    </p>
                  </div>
                ) : generatedPassword.webhookReason ? (
                  <div className="callout-box" style={{ backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }}>
                    <p className="text-sm font-medium" style={{ color: '#92400e' }}>
                      ⚠ Welcome email did NOT send ({generatedPassword.webhookReason}). Copy these credentials and send them manually from clients@indielaw.com.
                    </p>
                  </div>
                ) : (
                  <div className="callout-box">
                    <p className="text-sm font-medium mb-1" style={{ color: '#141b4d' }}>Save these credentials — the password cannot be retrieved later.</p>
                    <p className="text-xs mt-2" style={{ color: '#52525B' }}>Best practice: click <strong>Copy</strong> rather than typing the password by hand.</p>
                  </div>
                )}
                <div className="space-y-3">
                  <div>
                    <label className="label-uppercase block mb-1">Client</label>
                    <p className="font-medium" style={{ color: '#101820' }}>{generatedPassword.name}</p>
                  </div>
                  <div>
                    <label className="label-uppercase block mb-1">Email</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-3 text-sm font-mono" style={{ backgroundColor: '#f2e9db', color: '#101820' }}>{generatedPassword.email}</code>
                      <button onClick={() => { navigator.clipboard.writeText(generatedPassword.email); toast.success('Email copied'); }}
                        className="btn-secondary px-3 py-3 text-sm">Copy</button>
                    </div>
                  </div>
                  <div>
                    <label className="label-uppercase block mb-1">Password</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-3 text-sm font-mono" style={{ backgroundColor: '#f2e9db', color: '#101820' }}>{generatedPassword.password}</code>
                      <button onClick={() => { navigator.clipboard.writeText(generatedPassword.password); toast.success('Password copied'); }}
                        className="btn-secondary px-3 py-3 text-sm">Copy</button>
                    </div>
                  </div>
                </div>
                <button onClick={() => setGeneratedPassword(null)} className="btn-primary w-full mt-4" data-testid="close-password-dialog">
                  Done
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Activation Confirmation Dialog */}
        <Dialog open={!!pendingActivation} onOpenChange={(open) => { if (!open) setPendingActivation(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold" style={{ color: '#141b4d' }}>
                Activate {pendingActivation?.name}?
              </DialogTitle>
            </DialogHeader>
            {pendingActivation && (
              <div className="space-y-4 mt-2" data-testid="activate-confirm-dialog">
                <p className="text-sm leading-relaxed" style={{ color: '#3f3f46' }}>
                  This will activate their client portal and email them login instructions
                  (including a fresh password) at{' '}
                  <strong style={{ color: '#101820' }}>{pendingActivation.email}</strong>.
                </p>
                <p className="text-xs leading-relaxed" style={{ color: '#52525B' }}>
                  A new password will be generated and sent via the configured Zapier webhook.
                  You'll also see the password on the next screen as a backup.
                </p>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setPendingActivation(null)}
                    className="btn-secondary flex-1"
                    data-testid="cancel-activate-btn"
                    disabled={togglingId === pendingActivation.id}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(pendingActivation.id)}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="confirm-activate-btn"
                    disabled={togglingId === pendingActivation.id}
                  >
                    {togglingId === pendingActivation.id ? (
                      <><div className="w-4 h-4 border-2 border-[#141b4d]/20 border-t-[#141b4d] rounded-full animate-spin"></div>Activating...</>
                    ) : (
                      'Activate & send email'
                    )}
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
